import { onLocaleChange, t } from "../i18n";
import type { Store } from "../state";
import { wrapRangeAsSlider } from "./custom-slider";

type ToastFn = (key: string, params?: Record<string, string | number>) => void;

export interface ReferenceOverlayDeps {
  store: Store;
  loadOverlayImage(file: Blob): Promise<ImageData>;
  toast?: ToastFn;
}

export interface ReferenceOverlayOptions {
  visibilityParent?: HTMLElement;
  triggerVariant?: "top-bar" | "file-menu";
}

const IMAGE_TYPE_PRIORITY = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/heic",
] as const;

function isEditableElement(element: Element | null): boolean {
  const editable = element?.closest("input, textarea, [contenteditable]");
  if (!editable) return false;
  if (editable.hasAttribute("contenteditable")) {
    return editable.getAttribute("contenteditable") !== "false";
  }
  return true;
}

function isEditableTarget(target: EventTarget | null): boolean {
  const targetElement = target instanceof Element ? target : null;
  const activeElement =
    document.activeElement instanceof Element ? document.activeElement : null;
  if (isEditableElement(targetElement) || isEditableElement(activeElement))
    return true;
  const activeDialog = document.querySelector<HTMLElement>(
    '[role="dialog"]:not([hidden]), .settings-popup:not([hidden])',
  );
  return (
    activeDialog !== null &&
    ((targetElement !== null && activeDialog.contains(targetElement)) ||
      (activeElement !== null && activeDialog.contains(activeElement)))
  );
}

function pickBestImageFile(items: DataTransferItemList): {
  file: File;
  type: string;
  multiple: boolean;
} | null {
  const imageItems = Array.from(items).filter(
    (item) => item.kind === "file" && item.type.startsWith("image/"),
  );
  if (imageItems.length === 0) return null;

  const chosen =
    IMAGE_TYPE_PRIORITY.map((type) =>
      imageItems.find((item) => item.type === type),
    ).find((item): item is DataTransferItem => item !== undefined) ??
    imageItems[0];
  if (!chosen) return null;

  const file = chosen.getAsFile();
  if (!file) return null;
  return {
    file,
    type: chosen.type || file.type,
    multiple: imageItems.length > 1,
  };
}

function pickBestImageFileFromFiles(files: FileList): {
  file: File;
  type: string;
  multiple: boolean;
} | null {
  const imageFiles = Array.from(files).filter((file) =>
    file.type.startsWith("image/"),
  );
  if (imageFiles.length === 0) return null;

  const chosen =
    IMAGE_TYPE_PRIORITY.map((type) =>
      imageFiles.find((file) => file.type === type),
    ).find((file): file is File => file !== undefined) ?? imageFiles[0];
  if (!chosen) return null;
  return { file: chosen, type: chosen.type, multiple: imageFiles.length > 1 };
}

function pickBestDroppedImageFile(dataTransfer: DataTransfer): {
  file: File;
  type: string;
  multiple: boolean;
} | null {
  if (dataTransfer.items.length > 0) {
    return pickBestImageFile(dataTransfer.items);
  }
  return pickBestImageFileFromFiles(dataTransfer.files);
}

function setButtonDisabled(button: HTMLButtonElement, disabled: boolean): void {
  button.disabled = disabled;
  if (disabled) {
    button.setAttribute("aria-disabled", "true");
  } else {
    button.removeAttribute("aria-disabled");
  }
}

export function createReferenceOverlayGroup(
  parent: HTMLElement,
  deps: ReferenceOverlayDeps,
  options: ReferenceOverlayOptions = {},
): () => void {
  const { store, loadOverlayImage } = deps;
  const toast = deps.toast ?? (() => {});

  const group = document.createElement("div");
  const triggerVariant = options.triggerVariant ?? "top-bar";
  group.className =
    triggerVariant === "file-menu"
      ? "reference-overlay-group reference-overlay-group-file-menu"
      : "reference-overlay-group";
  parent.appendChild(group);

  const overlayButton = document.createElement("button");
  overlayButton.type = "button";
  overlayButton.className =
    triggerVariant === "file-menu"
      ? "file-menu-item reference-overlay-trigger"
      : "top-bar-btn reference-overlay-trigger";
  if (triggerVariant === "file-menu") {
    overlayButton.setAttribute("role", "menuitem");
    overlayButton.tabIndex = -1;
  }
  overlayButton.setAttribute(
    "aria-label",
    t("action.referenceOverlay.overlay"),
  );

  const face =
    triggerVariant === "file-menu"
      ? document.createElement("span")
      : overlayButton;
  if (triggerVariant === "file-menu") {
    face.className = "top-bar-btn-face";
  }

  const icon = document.createElement("span");
  icon.className =
    triggerVariant === "file-menu"
      ? "file-menu-item-icon top-bar-btn-icon reference-overlay-trigger-icon"
      : "top-bar-btn-icon reference-overlay-trigger-icon";
  icon.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className =
    triggerVariant === "file-menu"
      ? "file-menu-item-label top-bar-btn-label"
      : "top-bar-btn-label";
  label.dataset["i18n"] = "action.referenceOverlay.overlay";
  label.textContent = t("action.referenceOverlay.overlay");

  face.appendChild(icon);
  face.appendChild(label);
  if (triggerVariant === "file-menu") {
    overlayButton.appendChild(face);
  }
  group.appendChild(overlayButton);

  const visibilityToggle = document.createElement("button");
  visibilityToggle.type = "button";
  visibilityToggle.className = "top-bar-btn reference-overlay-visibility-toggle";

  const visibilityIcon = document.createElement("span");
  visibilityIcon.className = "top-bar-btn-icon reference-overlay-visibility-icon";
  visibilityIcon.setAttribute("aria-hidden", "true");
  visibilityToggle.appendChild(visibilityIcon);

  const visibilityLabel = document.createElement("span");
  visibilityLabel.className =
    "top-bar-btn-label reference-overlay-visibility-label";
  visibilityLabel.dataset["i18n"] = "action.referenceOverlay.overlay";
  visibilityLabel.textContent = t("action.referenceOverlay.overlay");
  visibilityToggle.appendChild(visibilityLabel);
  const visibilityParent =
    options.visibilityParent ?? (triggerVariant === "top-bar" ? group : null);
  visibilityParent?.appendChild(visibilityToggle);

  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.id = "ctrl-referenceOverlayFile";
  fileInput.accept = "image/png,image/*";
  fileInput.className = "sr-only";
  fileInput.setAttribute("aria-label", t("action.referenceOverlay.open"));
  group.appendChild(fileInput);

  const popup = document.createElement("div");
  popup.className = "settings-popup overlay-popup reference-overlay-popup";
  popup.setAttribute("role", "dialog");
  popup.hidden = true;
  document.body.appendChild(popup);

  const sourceSection = document.createElement("div");
  sourceSection.className = "settings-popup-section";

  const sourceLabel = document.createElement("div");
  sourceLabel.className = "settings-popup-label";
  sourceLabel.dataset["i18n"] = "label.referenceOverlay.source";
  sourceLabel.textContent = t("label.referenceOverlay.source");
  sourceSection.appendChild(sourceLabel);

  const ingestionTarget = document.createElement("button");
  ingestionTarget.type = "button";
  ingestionTarget.className = "reference-overlay-ingestion";
  ingestionTarget.setAttribute(
    "aria-label",
    t("action.referenceOverlay.ingest"),
  );

  const ingestionTitle = document.createElement("span");
  ingestionTitle.className = "reference-overlay-ingestion-title";
  ingestionTitle.dataset["i18n"] = "label.referenceOverlay.ingest.title";
  ingestionTitle.textContent = t("label.referenceOverlay.ingest.title");

  const ingestionHint = document.createElement("span");
  ingestionHint.className = "reference-overlay-ingestion-hint";
  ingestionHint.dataset["i18n"] = "label.referenceOverlay.ingest.hint";
  ingestionHint.textContent = t("label.referenceOverlay.ingest.hint");

  const ingestionMeta = document.createElement("span");
  ingestionMeta.className = "reference-overlay-ingestion-meta";
  ingestionMeta.dataset["i18n"] = "label.referenceOverlay.ingest.meta";
  ingestionMeta.textContent = t("label.referenceOverlay.ingest.meta");

  ingestionTarget.appendChild(ingestionTitle);
  ingestionTarget.appendChild(ingestionHint);
  ingestionTarget.appendChild(ingestionMeta);
  sourceSection.appendChild(ingestionTarget);

  const removeButton = document.createElement("button");
  removeButton.type = "button";
  removeButton.className = "btn btn-ghost reference-overlay-remove";
  removeButton.dataset["i18n"] = "action.referenceOverlay.remove";
  removeButton.textContent = t("action.referenceOverlay.remove");
  removeButton.setAttribute("aria-label", t("action.referenceOverlay.remove"));
  removeButton.hidden = true;
  sourceSection.appendChild(removeButton);
  popup.appendChild(sourceSection);

  const opacitySection = document.createElement("div");
  opacitySection.className =
    "settings-popup-section reference-overlay-opacity-section";

  const opacityLabel = document.createElement("label");
  opacityLabel.className = "reference-overlay-opacity-label";
  opacityLabel.htmlFor = "ctrl-referenceOverlayOpacity";
  opacityLabel.dataset["i18n"] = "label.referenceOverlay.opacity";
  opacityLabel.textContent = t("label.referenceOverlay.opacity");
  opacityLabel.hidden = true;
  let syncingOpacity = false;

  const opacityInput = document.createElement("input");
  opacityInput.type = "range";
  opacityInput.id = "ctrl-referenceOverlayOpacity";
  opacityInput.min = "0";
  opacityInput.max = "100";
  opacityInput.step = "1";
  opacityInput.value = "100";
  opacityInput.setAttribute("aria-label", t("label.referenceOverlay.opacity"));
  const opacitySlider = wrapRangeAsSlider(opacityInput, "violet");
  opacitySlider.classList.add("reference-overlay-opacity-slider");
  opacitySlider.hidden = true;
  opacitySection.appendChild(opacityLabel);
  opacitySection.appendChild(opacitySlider);
  popup.appendChild(opacitySection);

  async function applyOverlay(file: Blob): Promise<void> {
    const sourceAtStart = store.get().source;
    if (!sourceAtStart) return;
    try {
      const image = await loadOverlayImage(file);
      if (store.get().source !== sourceAtStart) return;
      const current = store.get().referenceOverlay;
      store.patch({
        referenceOverlay: {
          image,
          enabled: true,
          opacity: current.opacity,
        },
      });
    } catch {
      toast("toast.referenceOverlay.decodeFail");
    }
  }

  let isOpen = false;

  function positionPopup(): void {
    const anchorRect = overlayButton.getBoundingClientRect();
    const gap = 5;
    const viewportWidth = window.innerWidth;
    const popupWidth = popup.offsetWidth || 220;
    let left = anchorRect.left;

    if (left + popupWidth > viewportWidth - 8) {
      left = viewportWidth - popupWidth - 8;
    }
    if (left < 8) left = 8;

    popup.style.top = `${anchorRect.bottom + gap}px`;
    popup.style.left = `${left}px`;
  }

  function openPopup(): void {
    if (!store.get().source) return;
    isOpen = true;
    popup.hidden = false;
    overlayButton.setAttribute("aria-expanded", "true");
    sync();
    positionPopup();
  }

  function closePopup(): void {
    if (!isOpen) return;
    isOpen = false;
    popup.hidden = true;
    overlayButton.setAttribute("aria-expanded", "false");
    ingestionTarget.classList.remove("reference-overlay-ingestion-active");
  }

  function isIngestionTargetEvent(target: EventTarget | null): boolean {
    const targetElement = target instanceof Element ? target : null;
    const activeElement =
      document.activeElement instanceof Element ? document.activeElement : null;
    return (
      (targetElement !== null && ingestionTarget.contains(targetElement)) ||
      (activeElement !== null && ingestionTarget.contains(activeElement))
    );
  }

  function sync(): void {
    const state = store.get();
    const { image, enabled, opacity } = state.referenceOverlay;
    const hasSource = state.source !== null;
    const hasImage = hasSource && image !== null;
    if (!hasSource) closePopup();
    removeButton.hidden = !hasImage;
    opacityLabel.hidden = !hasImage;
    opacitySlider.hidden = !hasImage;
    overlayButton.setAttribute("aria-pressed", String(hasImage && enabled));
    overlayButton.setAttribute("aria-expanded", String(isOpen));
    visibilityToggle.setAttribute("aria-pressed", String(hasImage && enabled));
    const visibilityAriaKey =
      hasImage && enabled
        ? "action.referenceOverlay.hide"
        : "action.referenceOverlay.show";
    visibilityToggle.setAttribute("aria-label", t(visibilityAriaKey));
    visibilityToggle.title = t(visibilityAriaKey);
    setButtonDisabled(overlayButton, !hasSource);
    setButtonDisabled(visibilityToggle, !hasImage);
    setButtonDisabled(ingestionTarget, !hasSource);
    ingestionTarget.setAttribute("aria-disabled", String(!hasSource));
    fileInput.disabled = !hasSource;
    setButtonDisabled(removeButton, !hasImage);
    syncingOpacity = true;
    opacityInput.value = String(Math.round(opacity * 100));
    opacityInput.dispatchEvent(new Event("input"));
    syncingOpacity = false;
  }

  overlayButton.addEventListener("click", () => {
    if (!store.get().source) return;
    if (isOpen) closePopup();
    else openPopup();
  });

  visibilityToggle.addEventListener("click", () => {
    const current = store.get().referenceOverlay;
    if (!current.image) return;
    store.patch({
      referenceOverlay: {
        ...current,
        enabled: !current.enabled,
      },
    });
  });

  ingestionTarget.addEventListener("click", () => {
    if (!store.get().source) return;
    fileInput.value = "";
    fileInput.click();
  });

  fileInput.addEventListener("change", () => {
    if (!store.get().source) return;
    const file = fileInput.files?.[0];
    if (file) void applyOverlay(file);
  });

  removeButton.addEventListener("click", () => {
    store.patch({
      referenceOverlay: { image: null, opacity: 1, enabled: true },
    });
  });

  opacityInput.addEventListener("input", () => {
    if (syncingOpacity) return;
    const current = store.get().referenceOverlay;
    store.patch({
      referenceOverlay: {
        ...current,
        opacity: Math.min(1, Math.max(0, Number(opacityInput.value) / 100)),
      },
    });
  });

  ingestionTarget.addEventListener("dragover", (event) => {
    if (!store.get().source) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    ingestionTarget.classList.add("reference-overlay-ingestion-active");
  });

  ingestionTarget.addEventListener("dragleave", (event) => {
    const related =
      event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (related && ingestionTarget.contains(related)) return;
    ingestionTarget.classList.remove("reference-overlay-ingestion-active");
  });

  ingestionTarget.addEventListener("drop", (event) => {
    if (!store.get().source || !event.dataTransfer) return;
    const picked = pickBestDroppedImageFile(event.dataTransfer);
    if (!picked) return;
    event.preventDefault();
    ingestionTarget.classList.remove("reference-overlay-ingestion-active");
    if (picked.multiple) {
      toast("toast.referenceOverlay.multiItem", { type: picked.type });
    }
    void applyOverlay(picked.file);
  });

  const onPaste = (event: ClipboardEvent): void => {
    if (!store.get().source) return;
    if (!isIngestionTargetEvent(event.target) && isEditableTarget(event.target)) {
      return;
    }
    if (!event.clipboardData?.items) return;
    const picked = pickBestImageFile(event.clipboardData.items);
    if (!picked) return;
    event.preventDefault();
    if (picked.multiple) {
      toast("toast.referenceOverlay.multiItem", { type: picked.type });
    }
    void applyOverlay(picked.file);
  };

  const handleOutside = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (!popup.contains(target) && !group.contains(target)) {
      closePopup();
    }
  };

  const handleKey = (event: KeyboardEvent): void => {
    if (isOpen && event.key === "Escape") {
      event.stopPropagation();
      closePopup();
    }
  };

  const handleReposition = (): void => {
    if (isOpen) positionPopup();
  };

  document.addEventListener("paste", onPaste);
  document.addEventListener("click", handleOutside, true);
  document.addEventListener("keydown", handleKey, true);
  window.addEventListener("resize", handleReposition);
  window.addEventListener("scroll", handleReposition, { capture: true });
  const unsubscribe = store.subscribe((_, patch) => {
    if ("referenceOverlay" in patch || "source" in patch) sync();
  });

  onLocaleChange(() => {
    label.textContent = t("action.referenceOverlay.overlay");
    overlayButton.setAttribute(
      "aria-label",
      t("action.referenceOverlay.overlay"),
    );
    fileInput.setAttribute("aria-label", t("action.referenceOverlay.open"));
    sourceLabel.textContent = t("label.referenceOverlay.source");
    ingestionTarget.setAttribute(
      "aria-label",
      t("action.referenceOverlay.ingest"),
    );
    ingestionTitle.textContent = t("label.referenceOverlay.ingest.title");
    ingestionHint.textContent = t("label.referenceOverlay.ingest.hint");
    ingestionMeta.textContent = t("label.referenceOverlay.ingest.meta");
    removeButton.setAttribute(
      "aria-label",
      t("action.referenceOverlay.remove"),
    );
    removeButton.textContent = t("action.referenceOverlay.remove");
    visibilityLabel.textContent = t("action.referenceOverlay.overlay");
    opacityLabel.textContent = t("label.referenceOverlay.opacity");
    opacityInput.setAttribute(
      "aria-label",
      t("label.referenceOverlay.opacity"),
    );
    sync();
  });
  sync();

  return () => {
    document.removeEventListener("paste", onPaste);
    document.removeEventListener("click", handleOutside, true);
    document.removeEventListener("keydown", handleKey, true);
    window.removeEventListener("resize", handleReposition);
    window.removeEventListener("scroll", handleReposition, {
      capture: true,
    } as EventListenerOptions);
    unsubscribe?.();
    visibilityToggle.remove();
    group.remove();
    popup.remove();
  };
}
